import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Config } from './../config/config.service';
import {
  selectMicrositeSessionsIsLoggedIn,
  selectMicrositeSessionsLoading,
} from '../../NGRX/coreAuth/coreAuth.selectors';
import { AppState } from '../../NGRX/core.state';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';

@Injectable()
export class TicketsRouteGuard {
  rootUrl: string;

  constructor(
    private _config: Config,
    private _router: Router,
    private store: Store<AppState>
  ) {
    this.rootUrl = this._config.CONFIG.rootUrl;
  }

  /**
   * Guard that blocks access to the tickets routes for unauthenticated users.
   *
   * The original implementation used `filter(!!data) + take(1)` on
   * `selectMicrositeSessionsIsLoggedIn`. This was vulnerable to a race condition:
   * the store hydrates session state from localStorage synchronously, emitting
   * `isLoggedIn: true` before the server-side session validation runs. The guard
   * passed on this transient `true`, the route rendered, and only once the server
   * replied did a redirect fire — causing a visible 1-second flash of content.
   *
   * Fix: wait for `selectMicrositeSessionsLoading` to become `false` first
   * (i.e., the session check is definitively complete), then read auth state
   * once. Returning `false` also blocks Angular from activating the route at all;
   * the original `filter(!!data)` never-emitting pattern left the navigation in
   * a pending state rather than cancelling it.
   */
  canActivate(): Observable<boolean> {
    return this.store.select(selectMicrositeSessionsLoading).pipe(
      filter((isLoading) => !isLoading), // wait for session check to finish
      take(1),
      switchMap(() =>
        this.store.select(selectMicrositeSessionsIsLoggedIn).pipe(take(1))
      ),
      tap((isLoggedIn) => {
        if (!isLoggedIn) {
          this.prefetch(false);
        }
      }),
      map((isLoggedIn) => !!isLoggedIn),
      catchError(() => of(false))
    );
  }

  prefetch(data: boolean): void {
    if (!data) {
      this._router.navigate([this.rootUrl]);
      this._config.login();
    }
  }
}
